// Skills2D.tsx — max independence: per-label RNG, strong jitter, overdispersed gaps, shuffle spend
import React, { useMemo, useRef, useState, useEffect, useReducer } from "react";
import type { SkillNode } from "../types";
import { skillsRoot, buildIdIndex } from "../components/GalaxyScene";
import DrillsDialog from "./DrillsDialog"; // adjust path if needed
import { flattenLeaves } from "../utils/flatten";
import { Box } from "@mui/material";

type OnOpenCategory = (categoryId: string) => void;

// A "rider" is the little chip that travels from a ring label toward the glowing core.
// It remembers where it started (on the ring), where it’s headed (core edge),
// how long the path is, and how fast it moves.
type Rider = {
  id: number;
  fromId: string; // label (category) that emitted this rider
  title: string; // text to display inside the chip
  startX: number;
  startY: number; // world coords at the ring
  edgeX: number;
  edgeY: number; // world coords at the core boundary (not center)
  totalDist: number; // precomputed distance from start → edge
  speed: number; // px/sec (already includes global multipliers + per-spawn noise)
  bornAt: number; // performance.now() when this rider started moving
  arrived?: boolean; // set after reaching the core (used for a brief hold)
};

let RIDER_COUNTER = 1;
const DEBUG = false; // draws faint spokes from core to labels for layout debugging
// ───────── utils
// Collect the leaf skill titles under a node (used to round-robin which text a label emits)
function collectLeafTitles(n: SkillNode, out: string[] = []): string[] {
  const ch = (n as any)?.children as SkillNode[] | undefined;
  if (!ch || ch.length === 0) {
    if (n?.name) out.push(n.name);
    return out;
  }
  for (const c of ch) collectLeafTitles(c, out);
  return out;
}

// Collect leaf nodes + their "Category › Subcat › Leaf" path
type LeafWithPath = { node: SkillNode; path: string };

function collectLeavesWithPath(
  n: SkillNode,
  prefix: string,
  out: LeafWithPath[] = [],
): LeafWithPath[] {
  const kids = (n as any)?.children as SkillNode[] | undefined;
  const name = (n as any).name ?? "";
  const here = prefix || name;

  if (!kids || kids.length === 0) {
    out.push({ node: n, path: here });
    return out;
  }

  for (const c of kids) {
    const childName = (c as any).name ?? "";
    const childPath = here ? `${here} › ${childName}` : childName;
    collectLeavesWithPath(c, childPath, out);
  }
  return out;
}

// Middle-ellipsis for long rider text without expanding chip size
function truncateMiddle(s: string, max: number) {
  if (s.length <= max) return s;
  const keep = Math.max(1, max - 1),
    L = Math.ceil(keep / 2),
    R = keep - L;
  return s.slice(0, L) + "… " + s.slice(s.length - R);
}
// Fast 32-bit string hash used to seed per-label RNGs deterministically
function hash32(s: string) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
// Simple, cheap, deterministic PRNG (Mulberry32). One instance per label for independence.
function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
// Draw from an exponential distribution using the provided rng.
// We use this to construct “gamma-like” (overdispersed) inter-spawn gaps.
function expWith(rng: () => number) {
  const u = Math.max(1e-8, 1 - rng());
  return -Math.log(u);
}
// Cheap “gamma-like” gap generator: take Exp(1)^k with k<1 to get CV>1 (clumpy intervals).
// Mean is scaled by meanMs. Smaller k = more bursty, larger k → more Poisson-like.
function gammaLikeGap(rng: () => number, meanMs: number, k = 0.65) {
  const e = expWith(rng); // ~Exp(1)
  const scaled = Math.pow(e, k); // overdispersed
  return scaled * meanMs;
}
// Fisher–Yates shuffle (used to randomize which labels get to spend their queued tokens this frame)
function shuffleInPlace<T>(a: T[], rng: () => number) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
}

function riderRenderInfo(r: Rider, now: number) {
  if (r.arrived)
    return {
      arrived: true as const,
      px: r.edgeX,
      py: r.edgeY,
      scale: 1,
      opacity: 0,
    };

  const t = Math.max(
    0,
    Math.min(1, (r.speed * Math.max(0, now - r.bornAt)) / 1000 / r.totalDist),
  );

  const px = r.startX + (r.edgeX - r.startX) * t;
  const py = r.startY + (r.edgeY - r.startY) * t;

  const mid = 0.5;
  const up = t <= mid ? t / mid : 1;
  const down = t > mid ? 1 - (t - mid) / (1 - mid) : 1;
  const bell = Math.max(0, Math.min(up, down));

  const travelScale = 0.78 + 0.42 * bell;

  const fadeStart = 0.96;
  const fadePortion = t > fadeStart ? (t - fadeStart) / (1 - fadeStart) : 0;
  const fade = 1 - fadePortion * fadePortion;

  return {
    arrived: false as const,
    px,
    py,
    scale: travelScale * fade,
    opacity: Math.max(0.72, 0.96 * fade),
  };
}

const RiderOverlay = React.memo(function RiderOverlay({
  riders,
  coreCenterPctY,
  riderTextMaxLen,
  isSmall,
  isDesktop,
}: {
  riders: Rider[];
  coreCenterPctY: number;
  riderTextMaxLen: number;
  isSmall: boolean;
  isDesktop: boolean;
}) {
  const [nowTick, setNowTick] = useState(0); // performance.now());

  // useEffect(() => {
  //   if (!riders.length) return;

  //   let raf = 0;
  //   const tick = () => {
  //     setNowTick(performance.now());
  //     raf = requestAnimationFrame(tick);
  //   };
  //   raf = requestAnimationFrame(tick);
  //   return () => cancelAnimationFrame(raf);
  // }, [riders.length]);

  useEffect(() => {
  let raf = 0;

  const tick = (now: number) => {
    setNowTick(now);
    raf = requestAnimationFrame(tick);
  };

  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}, []);
  // function riderRenderInfo(r: Rider, now: number) {
  //   if (r.arrived)
  //     return {
  //       arrived: true as const,
  //       px: r.edgeX,
  //       py: r.edgeY,
  //       scale: 1,
  //       opacity: 0,
  //     };

  //   const t = Math.max(
  //     0,
  //     Math.min(1, (r.speed * Math.max(0, now - r.bornAt)) / 1000 / r.totalDist)
  //   );

  //   const px = r.startX + (r.edgeX - r.startX) * t;
  //   const py = r.startY + (r.edgeY - r.startY) * t;

  //   const mid = 0.5;
  //   const up = t <= mid ? t / mid : 1;
  //   const down = t > mid ? 1 - (t - mid) / (1 - mid) : 1;
  //   const bell = Math.max(0, Math.min(up, down));

  //   const travelScale = 0.78 + 0.42 * bell;

  //   const fadeStart = 0.96;
  //   const fadePortion = t > fadeStart ? (t - fadeStart) / (1 - fadeStart) : 0;
  //   const fade = 1 - fadePortion * fadePortion;

  //   return {
  //     arrived: false as const,
  //     px,
  //     py,
  //     scale: travelScale * fade,
  //     opacity: Math.max(0.72, 0.96 * fade),
  //   };
  // }

  function truncateMiddle(s: string, maxLen: number) {
    if (s.length <= maxLen) return s;
    const keep = maxLen - 1;
    const left = Math.ceil(keep / 2);
    const right = Math.floor(keep / 2);
    return s.slice(0, left) + "…" + s.slice(s.length - right);
  }

  return (
    <>
      {riders.map((r) => {
        const info = riderRenderInfo(r, nowTick);
        if (info.arrived) return null;

        const { px, py, scale, opacity } = info;
        // const rpx = Math.round(px * 2) / 2;
        // const rpy = Math.round(py * 2) / 2;

        // const rpx = Math.round(px);
        // const rpy = Math.round(py);

        const rpx = px;
        const rpy = py;
        

        // const rscale = Math.round(scale * 1000) / 1000;
        const text = truncateMiddle(r.title, riderTextMaxLen);

        return (
          <div
            key={r.id}
            style={{
              position: "absolute",
              left: "50%",
              top: `${coreCenterPctY * 100}%`,
              transform: `translate3d(${rpx}px, ${rpy}px, 0) translate(-50%, -50%)`,
              willChange: "transform",
              padding: "6px 10px",
              border: "1px solid rgba(140,170,255,0.45)",

              borderRadius: 999,
              background: "rgba(30,40,80,0.95)",
              color: "#e6eeff",
              fontSize: isSmall || !isDesktop ? 8 : 13,
              fontWeight: 700,
              whiteSpace: "nowrap",
              opacity,
              pointerEvents: "none",
            }}
            // style={{
            //   position: "absolute",
            //   left: "50%",
            //   top: `${coreCenterPctY * 100}%`,
            //   //transform: `translate3d(${rpx}px, ${rpy}px, 0) translate(-50%, -50%) scale(${rscale})`,
            //   transform: `translate3d(${rpx}px, ${rpy}px, 0) translate(-50%, -50%)`,
            //   willChange: "transform, opacity",
            //   transformOrigin: "50% 50%",
            //   padding: "6px 10px",
            //   borderRadius: 999,
            //   border: "1px solid rgba(140,170,255,0.45)",
            //   background: "rgba(30,40,80,0.8)",
            //   color: "#e6eeff",
            //   fontSize: isSmall || !isDesktop ? 8 : 13,
            //   fontWeight: 700,
            //   letterSpacing: 0.2,
            //   whiteSpace: "nowrap",
            //   opacity,
            //   pointerEvents: "none",
            //   boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
            // }}
          >
            {text}
          </div>
        );
      })}
      {/* <RiderOverlay
  riders={riders}
  coreCenterPctY={coreCenterPctY}
  riderTextMaxLen={riderTextMaxLen}
  isSmall={isSmall}
  isDesktop={isDesktop}
/> */}
    </>
  );
});

export default function Skills2D({
  onOpenCategory,
  // ring / labels
  isSmall,
  isLandscape,
  isDesktop,
  radiusPx = 300, // nominal ring radius (can auto-grow to prevent overlap)
  gapPx = 12, // angular neighbor spacing target when auto-growing
  startAngleDeg = -90, // where the first label sits (top)
  clockwise = true, // label ordering direction
  fontSpec = "700 14px system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif",
  chipHPadPx = 24, // extra horizontal padding used to estimate chip footprint
  chipHeightPx = 30, // ditto vertical; both feed collision-free radius calc
  autoGrowEqualAngles = false, // if true, increase ring radius until neighbor chips can fit
  maxGrowPx = 60, // cap for the auto-grow
  // core / alignment
  coreCenterPctY = 0.44, // vertical placement of the core (as % of container height)
  coreRadiusPx = 90, // visual radius of the glowing core
  // density
  riderDensity = 1.0, // multiplies both lanes-per-label and spawn rates
  riderPerLabelMax = 2, // concurrent riders per label (before density multiplier)
  maxRidersGlobal = 180, // global cap to keep DOM light
  // spawn cadence
  riderReleaseBaseMs = 1600, // baseline mean gap between spawns per label
  releaseMsByLabel, // optional: per-label overrides (by id or title)
  initialBurstPerLabel = 0, // 1, // warm-start count spawned mid-path per label
  // speed
  riderBaseSpeedPxPerSec = 10, // baseline rider speed
  riderSpeedMultiplier = 0.7, // global speed knob
  // rider text
  riderTextMaxLen = 28, // middle-ellipsis cutoff
}: {
  isSmall: boolean;
  isLandscape: boolean;
  isDesktop: boolean;
  onOpenCategory: OnOpenCategory;
  radiusPx?: number;
  gapPx?: number;
  startAngleDeg?: number;
  clockwise?: boolean;
  fontSpec?: string;
  chipHPadPx?: number;
  chipHeightPx?: number;
  autoGrowEqualAngles?: boolean;
  maxGrowPx?: number;
  coreCenterPctY?: number;
  coreRadiusPx?: number;
  riderDensity?: number;
  riderPerLabelMax?: number;
  maxRidersGlobal?: number;
  riderReleaseBaseMs?: number;
  releaseMsByLabel?: Record<string, number>;
  initialBurstPerLabel?: number;
  riderBaseSpeedPxPerSec?: number;
  riderSpeedMultiplier?: number;
  riderTextMaxLen?: number;
}) {
  const minProgressBeforeNextSpawn = 0.2; //0.62; // how far along the path a rider must be before its label can spawn another (prevents bunching at the start)

  const HOLE_ROWS = [
    { yNorm: -0.75, count: 8 }, // top
    { yNorm: -0.375, count: 8 },
    { yNorm: 0.0, count: 8 }, // middle
    { yNorm: 0.375, count: 8 },
    { yNorm: 0.75, count: 8 }, // bottom
  ];

  const PICKLEBALL_IMG = "pbskills/pickleball_no_logo.png";

  //   const isMobileMode = (!isDesktop); //isSmall || (!isDesktop && isLandscape);

  const isMobileMode = isSmall || !isDesktop; // phones + tablets

  const { holePositions } = useMemo(() => {
    // slightly inside the edge
    const R = coreRadiusPx * 0.8;
    const baseHoleR = coreRadiusPx * 0.09;

    const holePositions: {
      x: number;
      y: number;
      radius: number;
    }[] = [];

    HOLE_ROWS.forEach(({ yNorm, count }, rowIndex) => {
      const y = yNorm * R; // row’s vertical position
      const ringRadius = Math.sqrt(Math.max(0, R * R - y * y)); // circle eqn

      const step = (2 * Math.PI) / count;
      // slight phase offset every other row so they aren’t on perfect columns
      const phase = rowIndex % 2 === 0 ? 0 : step / 2;

      for (let i = 0; i < count; i++) {
        const th = phase + i * step;
        const x = Math.cos(th) * ringRadius;
        holePositions.push({
          x,
          y,
          radius: baseHoleR,
        });
      }
    });

    return { holePositions };
  }, [coreRadiusPx]);

  // data roots
  // Grab the skills tree and ensure the id-index exists (used elsewhere in the app).
  const root = skillsRoot as unknown as SkillNode;
  useMemo(() => buildIdIndex(root), [root]);

  // measure
  // A hidden canvas is used to measure label text width so we can grow the ring enough
  // that adjacent labels don't visually collide when autoGrowEqualAngles=true.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const measureWidth = (text: string) => {
    if (!canvasRef.current)
      canvasRef.current = document.createElement("canvas");
    const ctx = canvasRef.current.getContext("2d")!;
    ctx.font = fontSpec;
    return Math.ceil(ctx.measureText(text).width);
  };

  // categories (top-level children of the skills tree, minus Social & Enjoyment)
  const cats = useMemo(() => {
    const children = (root.children ?? []) as SkillNode[];
    return children
      .map((n) => ({ id: (n as any).id as string, title: n.name, node: n }))
      .filter((o) => o.title !== "Social & Enjoyment");
  }, [root]);
  const N = cats.length;

  // ring radius actually used: either the provided radius or a larger one if needed
  // so two neighboring chips with padding + gapPx could fit at equal angular spacing.
  const rUsed = useMemo(() => {
    if (!autoGrowEqualAngles || N <= 1) return radiusPx;
    const DELTA = (2 * Math.PI) / N,
      sinHalf = Math.sin(DELTA / 2);
    const rChip = cats.map(
      (c) => 0.5 * Math.hypot(measureWidth(c.title) + chipHPadPx, chipHeightPx),
    );
    let rMin = 0;
    for (let i = 0; i < N; i++) {
      const j = (i + 1) % N;
      // chord length needed between neighbors becomes (chip_i + chip_j + gap)
      rMin = Math.max(
        rMin,
        (rChip[i] + rChip[j] + gapPx) / (2 * Math.max(1e-6, sinHalf)),
      );
    }
    return Math.min(radiusPx + maxGrowPx, Math.max(radiusPx, rMin));
  }, [
    autoGrowEqualAngles,
    N,
    radiusPx,
    maxGrowPx,
    cats,
    chipHPadPx,
    chipHeightPx,
    gapPx,
    measureWidth,
  ]);

  //   // Label positions around the ring at equal angles, starting at startAngleDeg and
  //   // going clockwise or counterclockwise depending on the prop.
  //   const labels = useMemo(() => {
  //     if (N === 0) return [] as Array<{id:string; title:string; node:SkillNode; x:number; y:number;}>;
  //     const DELTA = (2 * Math.PI) / N;
  //     const start = (startAngleDeg * Math.PI) / 180;
  //     const dir = clockwise ? 1 : -1;
  //     return cats.map((c, i) => {
  //       const th = start + dir * i * DELTA;

  //       console.log({title: c.title, th, angle: th*180/2*Math.PI});

  //       return { id: c.id, title: c.title, node: c.node, x: Math.cos(th) * rUsed, y: Math.sin(th) * rUsed };
  //     });
  //   }, [cats, N, rUsed, startAngleDeg, clockwise]);

  // try ellipse-based
  // How "stretched" the ring is in X/Y.
  // 1 = circle, >1 = pushed farther out in that axis.
  const ELLIPSE_X_FACTOR = isSmall || !isDesktop ? 1.2 : 1.5; // a bit more to the sides
  const ELLIPSE_Y_FACTOR = isSmall || !isDesktop ? 1.3 : 0.9; // a bit flatter vertically
  const BULGE = 0.15; // 0 = none, 0.2 = noticeable // at sides

  const labels = useMemo(() => {
    if (N === 0)
      return [] as Array<{
        id: string;
        title: string;
        node: SkillNode;
        x: number;
        y: number;
      }>;

    // ───────────────────────────────
    // MOBILE / SMALL: side stacks
    // ───────────────────────────────
    if (isMobileMode) {
      //} (isSmall || !isDesktop) {
      const perSide = Math.ceil(N / 2);
      const verticalSpan = rUsed * 1.2; // how tall each stack is
      const step = perSide > 1 ? (verticalSpan * 2) / (perSide - 1) : 0;
      const sideRadiusX = rUsed * 1.05; // distance from center to labels

      let leftRow = 0;
      let rightRow = 0;

      const out: {
        id: string;
        title: string;
        node: SkillNode;
        x: number;
        y: number;
      }[] = [];

      cats.forEach((c, idx) => {
        const isLeft = idx % 2 === 0; // alternate left/right
        const rowIndex = isLeft ? leftRow++ : rightRow++;

        const y = -verticalSpan + rowIndex * step;
        const x = isLeft ? -sideRadiusX : sideRadiusX;

        out.push({
          id: c.id,
          title: c.title,
          node: c.node,
          x,
          y,
        });
      });

      return out;
    }
    // ─────────────────────────────────────────────
    // DESKTOP / LARGE: ellipse-based ring
    // ─────────────────────────────────────────────
    const DELTA = (2 * Math.PI) / N;
    const start = (startAngleDeg * Math.PI) / 180;
    const dir = clockwise ? 1 : -1;

    // Ellipse radii based on rUsed
    const a = rUsed * ELLIPSE_X_FACTOR; // horizontal radius
    const b = rUsed * ELLIPSE_Y_FACTOR; // vertical radius

    return cats.map((c, i: number) => {
      const th = start + dir * i * DELTA;

      const sideiness = Math.abs(Math.sin(th)); // 0 at top/bottom, 1 at pure side
      const aDynamic = a * (1 + BULGE * sideiness);
      const x = Math.cos(th) * aDynamic;
      const y = Math.sin(th) * b;

      return {
        id: c.id,
        title: c.title,
        node: c.node,
        x,
        y,
      };
    });
  }, [
    N,
    cats,
    rUsed,
    startAngleDeg,
    clockwise,
    isSmall,
    isDesktop,
    ELLIPSE_X_FACTOR,
    ELLIPSE_Y_FACTOR,
  ]);

  const labels_orig = useMemo(() => {
    if (N === 0)
      return [] as Array<{
        id: string;
        title: string;
        node: SkillNode;
        x: number;
        y: number;
      }>;

    const DELTA = (2 * Math.PI) / N;
    const start = (startAngleDeg * Math.PI) / 180;
    const dir = clockwise ? 1 : -1;

    // Ellipse radii based on rUsed
    const a = rUsed * ELLIPSE_X_FACTOR * (isSmall || !isDesktop ? 0.6 : 1); // horizontal radius
    const b = rUsed * ELLIPSE_Y_FACTOR * (isSmall || !isDesktop ? 0.7 : 1); // vertical radius

    return cats.map((c, i: number) => {
      const th = start + dir * i * DELTA;

      // Standard parametric ellipse:
      // x = a cos θ, y = b sin θ
      // const x = Math.cos(th) * a;
      const sideiness = Math.abs(Math.sin(th)); // 0 at top/bottom, 1 at pure side
      const aDynamic = a * (1 + BULGE * sideiness);
      const x = Math.cos(th) * aDynamic;
      const y =
        Math.sin(th) * b + (i === 0 && (isSmall || !isDesktop) ? -40 : 0);

      return {
        id: c.id,
        title: c.title,
        node: c.node,
        x,
        y,
      };
    });
  }, [cats, N, rUsed, startAngleDeg, clockwise]);

  // riders state (React + mutable ref mirror). We mutate the ref on every frame for speed
  // and only call setRiders when the array reference actually changes.
  const [riders, setRiders] = useState<Rider[]>([]);
  const ridersRef = useRef<Rider[]>([]);
  useEffect(() => {
    ridersRef.current = riders;
  }, [riders]);

  // Derived knobs:
  //  - perLabelMax: live lane limit per label (density-aware)
  //  - baseMean: baseline expected ms between spawns per label (density-aware)
  //  - speedBase: final baseline speed before per-spawn randomness
  const perLabelMax = Math.max(1, Math.round(riderPerLabelMax * riderDensity));
  const baseMean = Math.max(
    40,
    riderReleaseBaseMs / Math.max(0.25, riderDensity),
  );
  const speedBase = Math.max(
    0.1,
    riderBaseSpeedPxPerSec * riderSpeedMultiplier,
  );

  // Per-label stochastic state:
  //  - rng: deterministic per-label random generator
  //  - mean0: static mean gap for this label (jittered once at init)
  //  - wobble: slow multiplicative factor (Ornstein–Uhlenbeck-ish) to vary mean over time
  //  - nextTs: wall-clock time (ms) when the next token should be produced
  //  - q: queued tokens waiting to be spent (kept small to avoid spikes)
  //  - active: current riders from this label
  //  - rr: round-robin index into that label’s leaf titles
  type LState = {
    rng: () => number;
    mean0: number; // static jittered mean (wide)
    wobble: number; // slow OU multiplier
    nextTs: number; // next event time
    q: number; // queued tokens
    active: number; // lanes in use
    rr: number; // rr idx for leaf titles
  };
  const stateById = useRef<Record<string, LState>>({});

  function riderProgress(r: Rider, now: number) {
    return Math.max(
      0,
      Math.min(1, (r.speed * Math.max(0, now - r.bornAt)) / 1000 / r.totalDist),
    );
  }

  // Build a Rider object for a given label and title with known start/end and speed.
  // Note: we aim at the *edge* of the core, not the center, so paths all end on the sphere.
  function makeRider(
    L: { id: string; title: string; node: SkillNode; x: number; y: number },
    title: string,
    bornAt: number,
    speedMult: number,
  ): Rider {
    const len = Math.hypot(L.x, L.y) || 1;
    const ux = L.x / len,
      uy = L.y / len;
    const edgeX = ux * coreRadiusPx,
      edgeY = uy * coreRadiusPx;
    const dx = edgeX - L.x,
      dy = edgeY - L.y;
    return {
      id: RIDER_COUNTER++,
      fromId: L.id,
      title,
      startX: L.x,
      startY: L.y,
      edgeX,
      edgeY,
      totalDist: Math.max(1, Math.hypot(dx, dy)),
      speed: speedBase * speedMult,
      bornAt,
    };
  }

  // Seed per-label clocks (independent RNGs + independent means), then warm-start with
  // a few riders already mid-journey so the scene doesn’t begin empty.
  useEffect(() => {
    const now = performance.now();
    const st: Record<string, LState> = {};
    for (const L of labels) {
      const meanIn =
        releaseMsByLabel?.[L.id] ?? releaseMsByLabel?.[L.title] ?? baseMean;
      const rng = mulberry32(hash32(L.id) ^ hash32(L.title));
      // “Wide static jitter”: each label gets a different mean in ~[0.55×, 1.65×] its base.
      const mean0 = meanIn * (0.55 + 1.1 * rng());
      const nextTs = now + rng() * mean0; // randomize the phase so labels don't align initially
      st[L.id] = { rng, mean0, wobble: 1.0, nextTs, q: 0, active: 0, rr: 0 };
    }
    stateById.current = st;

    // Warm start: for each label, pre-create ‘burst’ riders at different phases
    // along the path so you immediately see motion without a long ramp-up.
    const warm: Rider[] = [];
    for (const L of labels) {
      const s = stateById.current[L.id]!;
      const leafs = collectLeafTitles(L.node);
      const burst = Math.max(
        0,
        Math.round(initialBurstPerLabel * riderDensity),
      );
      for (let b = 0; b < burst; b++) {
        const ix = s.rr + b;
        const title = leafs.length ? leafs[ix % leafs.length] : L.title;
        // Create a “template” rider to compute path length/speed, then set bornAt in the past
        // so it appears mid-flight.
        const tmp = makeRider(L, title, now, 1);
        const p = 0.25 + s.rng() * 0.5; // choose a point ~25–75% along the path
        const bornAt = now - ((p * tmp.totalDist) / tmp.speed) * 1000;
        // Per-spawn speed noise, ~[0.75×, 1.45×]
        const speedMult = 0.75 + 0.7 * s.rng();
        warm.push(makeRider(L, title, bornAt, speedMult));
        s.active = Math.min(perLabelMax, s.active + 1);
      }
      s.rr += burst;
    }
    ridersRef.current = warm.slice(0, maxRidersGlobal);
    setRiders(ridersRef.current);
  }, [
    labels,
    baseMean,
    perLabelMax,
    coreRadiusPx,
    maxRidersGlobal,
    releaseMsByLabel,
    riderDensity,
    initialBurstPerLabel,
    speedBase,
  ]);

  // ───────── Core “pulse” effect
  // A simple decaying accumulator that bumps whenever a rider arrives at the core.
  const pulseRef = useRef(0);
  const lastPulseTs = useRef(performance.now());
  function tickPulse(now: number) {
    const dt = Math.max(0, (now - lastPulseTs.current) / 1000);
    lastPulseTs.current = now;
    // Exponential decay toward 0 (τ ~ 0.7s). Values are mapped to ease later.
    pulseRef.current *= Math.exp(-dt / 1.0);
  }
  // Nonlinear mapping so small pulses are visible but big pulses don’t blow out the glow.
  const pulseLevel = () => {
    const x = Math.max(0, Math.min(1, pulseRef.current));
    return 1 - (1 - x) * (1 - x);
  };

  // ───────── Main RAF loop
  // 1) Move riders forward. 2) When they arrive, free a lane and bump the core pulse.
  // 3) Independently, each label’s clock may enqueue tokens; we spend them in a shuffled order.
  useEffect(() => {
    let raf = 0;

    const step_old = (now: number) => {
      //tickPulse(now);

      // nowTickRef.current = now;

      // 1) advance riders; drop those that arrived (free their lanes)

      // beginning of step function change

      const adv: Rider[] = [];
      for (const r of ridersRef.current) {
        const t = Math.min(
          1,
          (r.speed * Math.max(0, now - r.bornAt)) / 1000 / r.totalDist,
        );
        if (t >= 1) {
          if (!r.arrived) {
            // On *first* arrival, bump pulse and free the label’s active lane
            pulseRef.current = Math.min(1.5, pulseRef.current + 0.8);
            const st = stateById.current[r.fromId];
            if (st) st.active = Math.max(0, st.active - 1);
            // We don’t keep arrived riders around (no lingering hold in this variant)
          }
        } else adv.push(r);
      }

      const hadArrivals = adv.length !== ridersRef.current.length;
      ridersRef.current = adv;

      // 2) each label updates its internal timer and queues small integer tokens
      //    (tokens are later converted into actual spawns subject to lane caps)
      for (const L of labels) {
        const st = stateById.current[L.id];
        if (!st) continue;
        const { rng } = st;

        // Ornstein–Uhlenbeck-like “wobble”: slow multiplicative drift on the mean
        // This prevents a label from feeling too stationary in its cadence.
        const theta = 0.18; // mean reversion strength (lower = slower)
        const sigma = 0.6; // volatility (higher = noisier)
        const dSec = 1 / 60; // approximate frame step
        const gauss =
          Math.sqrt(-2 * Math.log(Math.max(1e-8, rng()))) *
          Math.cos(2 * Math.PI * rng());
        st.wobble +=
          theta * (1 - st.wobble) * dSec + sigma * Math.sqrt(dSec) * gauss;
        st.wobble = Math.min(1.9, Math.max(0.5, st.wobble)); // clamp wobble to a sane range

        const meanNow = st.mean0 * st.wobble;

        // Consume as many “nextTs” as we passed this frame; increment a small queue.
        // Queue is capped to avoid explosive bursts after a long pause.
        while (st.nextTs <= now) {
          if (st.q < 5) st.q += 1; // small per-label backlog cap
          st.nextTs += gammaLikeGap(rng, meanNow, 0.6);
        }
      }

      // 3) Spend queued tokens in a random label order to break residual correlation.
      const order = labels.map((_, i) => i);
      // Intentionally use global Math.random here to avoid coupling with per-label RNG seeds.
      shuffleInPlace(order, Math.random);

      const toSpawn: Rider[] = [];

      const activeRiders = ridersRef.current.filter(
        (r) => riderProgress(r, now) < 1,
      );

      const hasBlockingRider = activeRiders.some(
        (r) => riderProgress(r, now) < minProgressBeforeNextSpawn,
      );

      // Only allow a new spawn if:
      // - there are no active riders, OR
      // - all active riders are already far enough down their path.
      if (!hasBlockingRider) {
        for (const idx of order) {
          const L = labels[idx];
          const st = stateById.current[L.id];
          if (!st) continue;

          if (
            st.q > 0 &&
            st.active < perLabelMax &&
            ridersRef.current.length + toSpawn.length < maxRidersGlobal
          ) {
            st.q -= 1;
            st.active += 1;

            const leafs = collectLeafTitles(L.node);
            const ix = st.rr;
            st.rr = ix + 1;
            const title = leafs.length ? leafs[ix % leafs.length] : L.title;

            const speedMult = 0.75 + 0.7 * st.rng();
            const bornAt = now - 16;

            toSpawn.push(makeRider(L, title, bornAt, speedMult));
            // Enforce at most one new rider per frame globally.
            break;
          }
        }
      }

      if (toSpawn.length) {
        ridersRef.current = [...ridersRef.current, ...toSpawn];
      }

      if (hadArrivals || toSpawn.length) {
        setRiders(ridersRef.current);
      }

      // end of step function change

      // new stuff step function change 11:40am

      // end new stuff step function change 11:40am

      raf = requestAnimationFrame(step);
    };

    const step = (now: number) => {
      // 1) advance riders; drop those that arrived (free their lanes)
      const prevCount = ridersRef.current.length;

      const adv: Rider[] = [];
      for (const r of ridersRef.current) {
        const t = Math.min(
          1,
          (r.speed * Math.max(0, now - r.bornAt)) / 1000 / r.totalDist,
        );

        if (t >= 1) {
          if (!r.arrived) {
            // On first arrival, bump pulse and free the label’s active lane
            pulseRef.current = Math.min(1.5, pulseRef.current + 0.8);
            const st = stateById.current[r.fromId];
            if (st) st.active = Math.max(0, st.active - 1);
          }
          // do not keep arrived riders
        } else {
          adv.push(r);
        }
      }

      ridersRef.current = adv;

      // 2) each label updates its internal timer and queues small integer tokens
      for (const L of labels) {
        const st = stateById.current[L.id];
        if (!st) continue;
        const { rng } = st;

        const theta = 0.18;
        const sigma = 0.6;
        const dSec = 1 / 60;
        const gauss =
          Math.sqrt(-2 * Math.log(Math.max(1e-8, rng()))) *
          Math.cos(2 * Math.PI * rng());

        st.wobble +=
          theta * (1 - st.wobble) * dSec + sigma * Math.sqrt(dSec) * gauss;
        st.wobble = Math.min(1.9, Math.max(0.5, st.wobble));

        const meanNow = st.mean0 * st.wobble;

        while (st.nextTs <= now) {
          if (st.q < 5) st.q += 1;
          st.nextTs += gammaLikeGap(rng, meanNow, 0.6);
        }
      }

      // 3) Spend queued tokens in a random label order
      const order = labels.map((_, i) => i);
      shuffleInPlace(order, Math.random);

      const toSpawn: Rider[] = [];

      // Global gate: don't let another rider start until existing riders
      // are far enough down their paths.
      let hasBlockingRider = false;
      for (const r of ridersRef.current) {
        const t = Math.min(
          1,
          (r.speed * Math.max(0, now - r.bornAt)) / 1000 / r.totalDist,
        );
        if (t < minProgressBeforeNextSpawn) {
          hasBlockingRider = true;
          break;
        }
      }

      if (!hasBlockingRider) {
        for (const idx of order) {
          const L = labels[idx];
          const st = stateById.current[L.id];
          if (!st) continue;

          if (
            st.q > 0 &&
            st.active < perLabelMax &&
            ridersRef.current.length + toSpawn.length < maxRidersGlobal
          ) {
            st.q -= 1;
            st.active += 1;

            const leafs = collectLeafTitles(L.node);
            const ix = st.rr;
            st.rr = ix + 1;
            const title = leafs.length ? leafs[ix % leafs.length] : L.title;

            const speedMult = 0.75 + 0.7 * st.rng();
            const bornAt = now - 16;

            toSpawn.push(makeRider(L, title, bornAt, speedMult));

            // at most one new rider per frame globally
            break;
          }
        }
      }

      if (toSpawn.length) {
        ridersRef.current = [...ridersRef.current, ...toSpawn];
      }

      const nextCount = ridersRef.current.length;
      if (nextCount !== prevCount) {
        setRiders(ridersRef.current);
      }

      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [
    labels,
    perLabelMax,
    maxRidersGlobal,
    releaseMsByLabel,
    baseMean,
    speedBase,
  ]);

  // const nowTickRef = useRef(performance.now());
  // const [, bumpFrame] = useReducer((n) => (n + 1) % 1000000, 0);

  const SHOW_RIDERS = !isMobileMode; //!isSmall && isDesktop;

  // useEffect(() => {
  //   if (!SHOW_RIDERS) return;
  //   if (!riders.length) return;

  //   let raf = 0;

  //   const paint = (now: number) => {
  //     nowTickRef.current = now;
  //     bumpFrame();
  //     raf = requestAnimationFrame(paint);
  //   };

  //   raf = requestAnimationFrame(paint);
  //   return () => cancelAnimationFrame(raf);
  // }, [SHOW_RIDERS, riders.length]);

  // Compute render-space values for a rider at a given time.
  // Returns position (px,py), a bell-curve scale that peaks mid-path, and a fade near arrival.
  function riderRenderInfo_old(r: Rider, now: number) {
    if (r.arrived)
      return {
        arrived: true as const,
        px: r.edgeX,
        py: r.edgeY,
        scale: 1,
        opacity: 0,
      };
    const t = Math.max(
      0,
      Math.min(1, (r.speed * Math.max(0, now - r.bornAt)) / 1000 / r.totalDist),
    );
    const px = r.startX + (r.edgeX - r.startX) * t;
    const py = r.startY + (r.edgeY - r.startY) * t;

    // Symmetric rise/fall around the midpoint for a “breathing” size effect.
    const mid = 0.5,
      up = t <= mid ? t / mid : 1,
      down = t > mid ? 1 - (t - mid) / (1 - mid) : 1;
    const travelScale = 1.25 * Math.max(0, Math.min(up, down));

    // Ease out opacity near arrival (last ~4% of the path)
    const fadeStart = 0.96,
      fadePortion = t > fadeStart ? (t - fadeStart) / (1 - fadeStart) : 0;
    const fade = 1 - fadePortion * fadePortion;

    return {
      arrived: false as const,
      px,
      py,
      scale: Math.max(0.35, travelScale * fade),
      opacity: Math.max(0.4, 0.95 * fade + 0.05),
    };
  }

  // Core visual parameters derived from the pulse accumulator.
  const p = pulseLevel();
  const coreScale =
    isSmall || isLandscape || !isDesktop ? 0.5 + 0.06 * p : 1 + 0.06 * p;
  const outerGlow = 28 + 42 * p;
  const innerGlow = 24 + 20 * p;

  // Hover state for ring labels (purely visual highlight)
  const [hotId, setHotId] = useState<string | null>(null);

  // NEW: random highlight + drills dialog state
  const [randomHighlightId, setRandomHighlightId] = useState<string | null>(
    null,
  );
  const randomIntervalRef = useRef<number | null>(null);

  // Label highlight while the random picker is "spinning"
  const [rouletteId, setRouletteId] = useState<string | null>(null);
  const rouletteTimerRef = useRef<number | null>(null);

  const [drillsOpen, setDrillsOpen] = useState(false);
  //const [drillLeaf, setDrillLeaf] = useState<SkillNode | null>(null);
  // store the same “row” shape LevelTableDialog passes; type it as `any` to avoid TS union fights
  const [drillLeaf, setDrillLeaf] = useState<any>(null);
  const [drillHierarchy, setDrillHierarchy] = useState("");

  useEffect(() => {
    return () => {
      if (rouletteTimerRef.current !== null) {
        window.clearTimeout(rouletteTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (randomIntervalRef.current != null) {
        window.clearInterval(randomIntervalRef.current);
      }
    };
  }, []);

  const handlePickRandom = React.useCallback(() => {
    if (!labels.length) return;

    // Kill any existing spin
    if (rouletteTimerRef.current !== null) {
      window.clearTimeout(rouletteTimerRef.current);
    }

    // 🔧 clear any previous highlight
    setRouletteId(null);
    setHotId(null);

    const SPIN_DURATION = 1100; // total spin time (ms)
    const STEP_MIN = 70; // min time between flashes
    const STEP_MAX = 180; // max time between flashes
    const start = performance.now();

    const spinStep = () => {
      const now = performance.now();
      const elapsed = now - start;

      if (elapsed >= SPIN_DURATION) {
        // Pick a final random label
        const chosenLabel = labels[Math.floor(Math.random() * labels.length)];
        setRouletteId(chosenLabel.id);

        // Pick a random leaf/skill under that label
        const leafNodes = collectLeafTitles(chosenLabel.node); // titles only
        // If you already have a better "random leaf" helper, plug it in here.
        const leaves = (chosenLabel.node.children ?? []) as SkillNode[];
        const flatLeaves = flattenLeaves(chosenLabel.node as SkillNode); // if you have this util
        const finalLeaf =
          flatLeaves[Math.floor(Math.random() * flatLeaves.length)];

        // Open drills dialog for that leaf
        setDrillLeaf({
          id: (finalLeaf.node as any).id,
          name: finalLeaf.node.name,
          level: (finalLeaf.node as any).level,
          tips: (finalLeaf.node as any).tips,
          drills: (finalLeaf.node as any).drills,
        });
        const parentToRoot = finalLeaf.path
          .split(" › ")
          .slice(0, -1)
          .reverse()
          .join(" ← ");
        setDrillHierarchy(parentToRoot);
        setDrillsOpen(true);

        // Let the highlight sit on the chosen label; you can clear it if you prefer:
        // setRouletteId(null);
        rouletteTimerRef.current = null;
        return;
      }

      // While spinning: pick a random label each step (non-sequential)
      const randomLabel = labels[Math.floor(Math.random() * labels.length)];
      setRouletteId(randomLabel.id);

      // Ease out: steps get slightly slower near the end
      const t = elapsed / SPIN_DURATION;
      const step = STEP_MIN + (STEP_MAX - STEP_MIN) * t; // lerp from fast to slower

      rouletteTimerRef.current = window.setTimeout(spinStep, step);
    };

    spinStep();
  }, [labels, setDrillLeaf, setDrillHierarchy, setDrillsOpen]);

  // ───────── Render
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "grid",
        placeItems: "center",
        userSelect: "none",
        overflow: "hidden",
        background: "#050815",
        //transform: isSmall || !isDesktop ? "translateY(-120px)" : "none",
      }}
    >
      <div
        style={{
          zIndex: 100000,
          position: "absolute",
          width: coreRadiusPx * 2,
          height: coreRadiusPx * 2,
          borderRadius: "50%",
          overflow: "hidden",
          //top: `${(coreCenterPctY - 0.5) * 100 + 50}%`,
          left: "50%", // CHANGED
          top: `${coreCenterPctY * 100}%`, // CHANGED
          transform: `translate(-50%, -50%) scale(${coreScale})`, // CHANGED
          //transform: `translateY(-50%) scale(${coreScale})`,
          transformOrigin: "50% 50%",

          // KEEP your nice glow behind the image
          //   background:
          //     "radial-gradient(circle at 30% 25%, #fffed0 0%, #f9ff9a 18%, #d6e24a 45%, #9db20e 75%, #627000 100%)",

          //       boxShadow: `
          //   0 0 ${outerGlow}px rgba(214,226,74,0.55),
          //   inset 0 0 ${coreRadiusPx * 0.25}px rgba(0,0,0,0.4)
          // `,
          //   transition: "box-shadow 0.12s ease-out",
        }}
      >
        <img
          src={PICKLEBALL_IMG}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
            pointerEvents: "none",

            // Matte rounding to blend into glow
            //filter: "brightness(1.04) contrast(1.04)",
          }}
        />
      </div>

      {/* Dashed guide ring showing where labels sit (purely visual aid) */}
      <div
        style={{
          position: "absolute",
          width: Math.round(rUsed * 2),
          height: Math.round(rUsed * 2),
          borderRadius: "50%",
          //   border: "1px dashed rgba(255,255,255,0.08)",
          top: `${coreCenterPctY * 100}%`,
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      />

      {/* Optional spokes for debugging label angles
      {DEBUG &&
        labels.map(({ id, x, y }) => {
          const angle = Math.atan2(y, x) * (180 / Math.PI);
          return (
            <div
              key={`debug-${id}`}
              style={{
                position: "absolute",
                left: "50%",
                top: `${coreCenterPctY * 100}%`,
                width: rUsed,
                height: 1,
                transform: `translate(-0%, -50%) rotate(${angle}deg)`,
                transformOrigin: "0% 50%",
                background: "rgba(255,255,255,0.8)",
                pointerEvents: "none",
              }}
            />
          );
        })} */}

      {SHOW_RIDERS &&
        labels.map(({ id, x, y }) => {
          // angle from center to this label
          const angleDeg = (Math.atan2(y, x) * 180) / Math.PI;

          // length of the spoke: from center to label on the ellipse
          const len = Math.sqrt(x * x + y * y); // you can do *0.97 to stop just short

          return (
            <div
              key={`debug-${id}`}
              style={{
                position: "absolute",
                left: "50%",
                top: `${coreCenterPctY * 100}%`,
                width: len, // <— was rUsed
                height: 1,
                transform: `translate(0, -50%) rotate(${angleDeg}deg)`,
                transformOrigin: "0% 50%",
                background: "rgba(255,255,255,0.8)",
                pointerEvents: "none",
              }}
            />
          );
        })}

      {labels.map(({ id, title, x, y }) => {
        const hoverActive = hotId === id;
        const rouletteActive = rouletteId === id;
        const isActive = hoverActive || rouletteActive;

        return (
          <div
            key={id}
            role="button"
            onClick={() => onOpenCategory(id)}
            onMouseEnter={() => setHotId(id)}
            onMouseLeave={() => setHotId((h) => (h === id ? null : h))}
            style={{
              maxWidth: isSmall || !isDesktop ? 50 : 300,
              // whiteSpace: isSmall

              position: "absolute",
              left: `calc(50% + ${x}px)`,
              top: `calc(${coreCenterPctY * 100}% + ${y}px)`,
              // transform: "translate(-50%, -50%)",
              transform: `translate(-50%, -50%) scale(${
                rouletteActive ? 1.06 : 1.0
              })`, // tiny pop when roulette

              padding: "6px 10px",
              borderRadius: 10,
              border: rouletteActive
                ? "2px solid rgba(255, 230, 150, 0.95)" // ⭐ spin look
                : `1px solid ${
                    hoverActive
                      ? "rgba(110,150,255,0.55)"
                      : "rgba(120,140,200,0.35)"
                  }`,
              background: rouletteActive
                ? "rgba(250, 220, 90, 0.25)" // ⭐ warm flash
                : hoverActive
                  ? "rgba(20,30,60,0.65)"
                  : "rgba(15,20,40,0.5)",
              color: rouletteActive
                ? "#fffbe0"
                : hoverActive
                  ? "#b8caff"
                  : "#dbe6ff",
              fontWeight: 700,
              fontSize: isSmall || !isDesktop ? 10 : 16,
              letterSpacing: 0.1,
              cursor: "pointer",
              //   whiteSpace: "nowrap",
              whiteSpace: isSmall || !isDesktop ? "normal" : "nowrap",

              textDecoration: "underline",
              textUnderlineOffset: "3px",
              boxShadow: rouletteActive
                ? "0 0 12px rgba(255, 245, 180, 0.8)" // ⭐ glow when spinning
                : hoverActive
                  ? "0 0 0 2px rgba(60,100,255,0.25)"
                  : "0 1px 3px rgba(0,0,0,0.25)",
              // backdropFilter: "blur(2px)",
              zIndex: 99999,
              transformOrigin: "50% 50%",
              transition: "transform 80ms ease-out, box-shadow 80ms ease-out",
            }}
            title={`Open skills for ${title}`}
          >
            {title
              .replace(" & ", "/")
              .replace(" Patterns", "")
              .replace("(Mid-court)", "")
              .replace("Shot Selection/", "")
              .replace("/Setup", "")
              .replace("/Drills", "")}
          </div>
        );
      })}

      {/* Riders (moving chips). Position/scale/opacity are derived from time along the path. */}
      {/* {riders.map((r) => {
        //const info = riderRenderInfo(r, nowTick);
        const info = riderRenderInfo(r, nowTickRef.current);
        if (info.arrived) return null;
        const { px, py, scale, opacity } = info;

        const rpx = Math.round(px * 2) / 2;
        const rpy = Math.round(py * 2) / 2;
        const rscale = Math.round(scale * 1000) / 1000;

        const text = truncateMiddle(r.title, riderTextMaxLen);
        return (
          <div
            key={r.id}
            style={{
              position: "absolute",
              //left: `calc(50% + ${px}px)`,
              // top: `calc(${coreCenterPctY * 100}% + ${py}px)`,
              // transform: `translate(-50%, -50%) scale(${scale})`,
              left: "50%",
              top: `${coreCenterPctY * 100}%`,
              //  transform: `translate3d(${px}px, ${py}px, 0) translate(-50%, -50%) scale(${scale})`,
              transform: `translate3d(${rpx}px, ${rpy}px, 0) translate(-50%, -50%) scale(${rscale})`,
              willChange: "transform, opacity",

              transformOrigin: "50% 50%",
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid rgba(140,170,255,0.45)",
              background: "rgba(30,40,80,0.8)",
              color: "#e6eeff",
              fontSize: isSmall || !isDesktop ? 8 : 13,
              fontWeight: 700,
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
              opacity,
              pointerEvents: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
              // backdropFilter: "blur(2px)",
            }}
          >
            {text}
          </div>
        );
      })} */}

      <RiderOverlay
        riders={riders}
        coreCenterPctY={coreCenterPctY}
        riderTextMaxLen={riderTextMaxLen}
        isSmall={isSmall}
        isDesktop={isDesktop}
      />

      {/* <div
  style={{
    position: "relative",
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    userSelect: "none",
    overflow: "hidden",
    background: "#050815",
  }}
> */}
      <Box
        sx={{
          position: "absolute",
          right: "20px",
          top: 12,
          color: "white",
          zIndex: "400000",
          maxWidth: "280px",
          width: "280px",
          display: { xs: "none", sm: "none", md: "block" },
        }}
      >
        <Box sx={{ fontStyle: "italic", marginTop: "15px" }}>
          {'"The lyf so short, '}
        </Box>
        <Box sx={{ fontStyle: "italic", paddingLeft: "20px" }}>
          {'the craft so long to lerne."'}
        </Box>
        <Box sx={{ textAlign: "left", marginTop: "4px" }}>
          {"- Chaucer, "}
          <span style={{ fontStyle: "italic" }}> Parlement of Foules</span>
          {", 1382"}
        </Box>
      </Box>
      {/* NEW: Pick Random button */}
      <button
        type="button"
        onClick={handlePickRandom}
        style={{
          position: "absolute",
          top: 12,
          left: 16,
          zIndex: 200000,
          padding: "6px 12px",
          borderRadius: 8,
          border: "1px solid rgba(140,170,255,0.6)",
          background: "rgba(10,18,40,0.9)",
          color: "#dbe6ff",
          fontWeight: 600,
          fontSize: 13,
          letterSpacing: 0.3,
          cursor: "pointer",
          //   transform: isSmall ? "translateY(115px)" : "none",
        }}
      >
        Pick Random Skill
      </button>

      <DrillsDialog
        isSmall={isSmall}
        isLandscape={isLandscape}
        open={drillsOpen}
        onClose={() => {
          setDrillsOpen(false);
          setRouletteId(null);
        }}
        leaf={drillLeaf}
        reverseHierarchy={drillHierarchy}
        onPickRandomAgain={handlePickRandom} // ⭐ NEW
      />
    </div>
  );
}
